import { KeyboardEvent } from "react"

import { DiscountByDay } from "pages/discount/src/discount-modals/day-based-discount/day-based-discount"
import { DiscountTemplateDataType } from "services/properties/discount"

export const isAdminForDiscount = (admin = false): number => (admin ? 1 : 10)

export const getMinDiscountAllowed = (
  isAdmin = false,
  isDefault = false,
  discountDataLength = 0,
): number =>
  // when isDefault is true OR discountDataLength === 0,
  // it means we’re creating the first default discount — apply admin logic
  isDefault || discountDataLength === 0 ? isAdminForDiscount(isAdmin) : 1 // otherwise, the minimum allowed discount is 1

export enum DiscountType {
  STANDARD = "STANDARD",
  DYNAMIC_DISCOUNT = "DYNAMIC_DISCOUNT",
  TIERED = "TIERED",
  EARLY_BIRD = "EARLY_BIRD",
  DAY_BASED = "DAY_BASED",
  EXTENDED_STAY = "EXTENDED_STAY",
  DYNAMIC_DISCOUNT_31 = "DYNAMIC_DISCOUNT_31",
  SEASONAL = "SEASONAL",
  NEW_LISTING = "NEW_LISTING",
  BOOKING_EXTENSION = "BOOKING_EXTENSION",
}

export const discountTypes = [
  {
    label: "Standard (0-30 days)",
    value: DiscountType.STANDARD,
    desc: "Discount bookings within 30 days",
    bgColor: "#C5FCCB",
    borderColor: "#9AF7A8",
  },
  {
    label: "Discount range (0-30 days)",
    value: DiscountType.DYNAMIC_DISCOUNT,
    desc: "Discount range for bookings within 30 days",
    bgColor: "#DFA6E2",
    borderColor: "#C97CCD",
  },
  {
    label: "Tiered",
    value: DiscountType.TIERED,
    desc: "Different discounts by booking window",
    bgColor: "#CEFAFE",
    borderColor: "#A5F3FC",
  },
  {
    label: "Early bird (31+ days)",
    value: DiscountType.EARLY_BIRD,
    desc: "Discount advance bookings",
    bgColor: "#FFBE8C",
    borderColor: "#FCD8B5",
  },
  {
    label: "Day-based",
    value: DiscountType.DAY_BASED,
    desc: "Discount specific days of the week",
    bgColor: "#CECEFE",
    borderColor: "#A7A5FC",
  },
  {
    label: "Extended stay",
    value: DiscountType.EXTENDED_STAY,
    desc: "Discount longer trips",
    bgColor: "#FECEDF",
    borderColor: "#FCA5C2",
  },
  {
    label: "Discount range (Custom Dates)",
    value: DiscountType.DYNAMIC_DISCOUNT_31,
    desc: "Discount range for bookings on specific advance dates",
    bgColor: "#F5E6D3",
    borderColor: "#DEB887",
  },
  {
    label: "Seasonal",
    value: DiscountType.SEASONAL,
    desc: "Discount specific dates",
    bgColor: "#FCFECE",
    borderColor: "#D6DAA0",
  },
  {
    label: "New listing",
    value: DiscountType.NEW_LISTING,
    desc: "Discount your first bookings",
    bgColor: "#B5DAFF",
    borderColor: "#5E94CE",
  },
  {
    label: "Stay extension",
    value: DiscountType.BOOKING_EXTENSION,
    desc: "Discount applied when a guest extends their current stay.",
    bgColor: "#e2fb97",
    borderColor: "#bae115",
  },
]

export enum DiscountStatus {
  ACTIVE = "ACTIVE",
  PAUSED = "PAUSED",
}

export const dayIndexMap: Record<string, number> = {
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6,
  Sun: 7,
}

// Utility to map numeric day (1–7) → string name
export const dayMap: Record<number, string> = {
  1: "Mon",
  2: "Tue",
  3: "Wed",
  4: "Thu",
  5: "Fri",
  6: "Sat",
  7: "Sun",
}

// Days of the week (short form)
export const daysOfWeek = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"]
export const weekDays = ["Sun", "Mon", "Tue", "Wed", "Thu"]
export const weekendDays = ["Fri", "Sat"]

export const dayFullNames: Record<string, string> = {
  Sun: "Sunday",
  Mon: "Monday",
  Tue: "Tuesday",
  Wed: "Wednesday",
  Thu: "Thursday",
  Fri: "Friday",
  Sat: "Saturday",
}

export const getDayDiscountValue = (
  day: string,
  discounts: DiscountByDay,
  individualEnabled: boolean,
): number | "" => {
  if (!individualEnabled) {
    if (weekDays.includes(day)) {
      return discounts.Weekdays ?? discounts[day] ?? 0
    }
    if (weekendDays.includes(day)) {
      return discounts.Weekend ?? discounts[day] ?? 0
    }
  } else if (discounts[day] !== undefined && discounts[day] !== "") {
    return discounts[day]
  }
  return 0
}

export function getDiscountDescription(
  discount: DiscountTemplateDataType,
): string {
  switch (discount.discountType) {
    case DiscountType.STANDARD: {
      const percent = discount.tieredDiscountList?.[0]?.discount_percent ?? 0
      return `${percent}% off • 0-30 days`
    }

    case DiscountType.DYNAMIC_DISCOUNT: {
      const minDiscount = discount.tieredDiscountList?.[0]?.min_discount ?? 0
      const maxDiscount = discount.tieredDiscountList?.[0]?.max_discount ?? 0
      // Display dynamic discount range with stay window: "31+ days" [DiscountType.DYNAMIC_DISCOUNT_31] if min_stay is 31 ,
      // otherwise "0–30 days" [DiscountType.DYNAMIC_DISCOUNT].
      if (
        discount.discountType === DiscountType.DYNAMIC_DISCOUNT &&
        discount?.tieredDiscountList?.[0]?.min_stay === 31
      ) {
        const tiers = discount.tieredDiscountList ?? []
        const mins = tiers
          .map((t) => Number(t.min_discount))
          .filter((n): n is number => !Number.isNaN(n))

        const maxs = tiers
          .map((t) => Number(t.max_discount))
          .filter((n): n is number => !Number.isNaN(n))

        const min = mins.length ? Math.min(...mins) : 0
        const max = maxs.length ? Math.max(...maxs) : 0

        const names =
          discount.tieredDiscountList
            ?.map((t) => t.name)
            .filter((n): n is string => !!n) ?? []

        const nameText = names.length ? ` • ${names.join(", ")}` : ""
        const percentText = min === max ? `${min}%` : `${min}-${max}%`

        return `${percentText} off${nameText}`
      }
      // otherwise "0–30 days" [DiscountType.DYNAMIC_DISCOUNT].
      return `${minDiscount}-${maxDiscount}% off • 0-30 days`
    }

    case DiscountType.TIERED: {
      const percents =
        discount.tieredDiscountList
          ?.map((t) => t.discount_percent)
          .filter((p): p is number => p !== null && p !== undefined) ?? []

      if (percents.length > 0) {
        const min = Math.min(...percents)
        const max = Math.max(...percents)
        return min === max ? `${min}% off` : `${min}-${max}% off`
      }
      return "0% off"
    }

    case DiscountType.EARLY_BIRD: {
      const percents =
        discount.tieredDiscountList
          ?.map((t) => t.discount_percent)
          .filter((p): p is number => p !== null && p !== undefined) ?? []
      const max = percents.length ? Math.max(...percents) : 0

      const formatStay = (minStay: number | null, maxStay: number | null) => {
        if (minStay && maxStay === null) {
          return `${minStay}+ days`
        }
        if (minStay && maxStay) {
          return `${minStay}-${maxStay} days`
        }
        return null
      }
      const stays =
        discount.tieredDiscountList
          ?.map((t) => formatStay(t.min_stay, t.max_stay))
          .filter((s): s is string => !!s) ?? []

      const stayText = stays.length ? ` • ${stays.join(", ")}` : ""
      return `0-${max}% off${stayText}`
    }

    case DiscountType.DAY_BASED: {
      const percents =
        discount.tieredDiscountList
          ?.map((t) => Number(t.discount_percent))
          .filter((p): p is number => !Number.isNaN(p)) ?? []

      const max = percents.length ? Math.max(...percents) : 0

      const days =
        discount.tieredDiscountList
          ?.map((t) => (t.day && dayMap[t.day] ? dayMap[t.day] : null))
          .filter((d): d is string => !!d) ?? []

      // Sort based on weekOrder (Sun → Sat)
      const sortedDays = days.sort(
        (a, b) => daysOfWeek.indexOf(a) - daysOfWeek.indexOf(b),
      )

      const dayText = sortedDays.length ? ` • ${sortedDays.join(", ")}` : ""
      return `${max}% off${dayText}`
    }

    case DiscountType.EXTENDED_STAY: {
      const percents =
        discount.tieredDiscountList
          ?.map((t) => t.discount_percent)
          .filter((p): p is number => p !== null && p !== undefined) ?? []
      const min = percents.length ? Math.min(...percents) : 0
      const max = percents.length ? Math.max(...percents) : 0

      const stays =
        discount.tieredDiscountList
          ?.map((t) => (t.min_stay ? `${t.min_stay}+ nights` : null))
          .filter((s): s is string => !!s) ?? []

      const stayText = stays.length ? ` • ${stays.join(", ")}` : ""
      const percentText = min === max ? `${min}%` : `${min}-${max}%`
      return `${percentText} off${stayText}`
    }

    case DiscountType.SEASONAL: {
      const percents =
        discount.tieredDiscountList
          ?.map((t) => Number(t.discount_percent))
          .filter((p): p is number => !Number.isNaN(p)) ?? []
      const min = percents.length ? Math.min(...percents) : 0
      const max = percents.length ? Math.max(...percents) : 0

      const names =
        discount.tieredDiscountList
          ?.map((t) => t.name)
          .filter((n): n is string => !!n) ?? []

      const nameText = names.length ? ` • ${names.join(", ")}` : ""
      const percentText = min === max ? `${min}%` : `${min}-${max}%`
      return `${percentText} off${nameText}`
    }

    case DiscountType.NEW_LISTING: {
      const firstTier = discount.tieredDiscountList?.[0]
      if (!firstTier) return discount.discountTemplateName || "New listing"
      return `${Number(firstTier.discount_percent) || 0}% off • First ${
        firstTier.number_of_bookings ?? 0
      } ${(firstTier.number_of_bookings || 0) > 1 ? "bookings" : "booking"} ${
        (firstTier?.newListingUsedCount ?? 0) > 0
          ? `(${firstTier?.newListingUsedCount ?? 0} used)`
          : ""
      }`
    }

    default:
      return discount.discountTemplateName || ""
  }
}

export const DiscountTypeLabels: Record<DiscountType, string> = {
  [DiscountType.STANDARD]: "Standard 0-30",
  [DiscountType.DYNAMIC_DISCOUNT]: "Discount range 0-30",
  [DiscountType.TIERED]: "Tiered",
  [DiscountType.EARLY_BIRD]: "Early bird",
  [DiscountType.DAY_BASED]: "Day based",
  [DiscountType.EXTENDED_STAY]: "Extended stay",
  [DiscountType.DYNAMIC_DISCOUNT_31]: "Discount range 31+",
  [DiscountType.SEASONAL]: "Seasonal",
  [DiscountType.NEW_LISTING]: "New listing",
  [DiscountType.BOOKING_EXTENSION]: "Stay extension",
}

export function parseActiveDiscountList(
  list?: string[],
  isLower = false,
  useShortStandard = false,
) {
  if (!Array.isArray(list)) return [] // <--- always return []

  return list.map((item) => {
    const [discountName, discountTypeRaw, isDefaultRaw] = item.split("||")

    // parse isDefault boolean safely
    const isDefault = String(isDefaultRaw)?.trim()?.toLowerCase() === "true"

    // invalid types
    const discountType =
      !discountTypeRaw ||
      discountTypeRaw === "null" ||
      discountTypeRaw === "undefined"
        ? ""
        : discountTypeRaw

    let discountLabel =
      (discountType
        ? DiscountTypeLabels[discountType as DiscountType] || discountType
        : "") || ""

    // set "Standard" case instead of "Standard 0-30" for Standard
    if (useShortStandard && discountType === DiscountType.STANDARD) {
      discountLabel = "Standard"
    }

    return {
      discountName: discountName || "",
      discountType: isLower
        ? discountLabel.toLowerCase().replace(/\s+/g, "")
        : discountLabel,
      isDefault,
    }
  })
}

export const preventInvalidNumberKeys = (
  e: KeyboardEvent<HTMLInputElement>,
) => {
  const allowedKeys = [
    "Backspace",
    "Tab",
    "Escape",
    "Enter",
    "ArrowLeft",
    "ArrowRight",
    "ArrowUp",
    "ArrowDown",
    "Delete",
    "Home",
    "End",
  ]

  if (allowedKeys.includes(e.key) || e.ctrlKey || e.metaKey) {
    return
  }

  // Allow only digits and a single decimal point
  if (!/^[\d.]$/.test(e.key)) {
    e.preventDefault()
    return
  }

  // If decimal point is pressed, prevent if there's already one
  if (e.key === ".") {
    const target = e.target as HTMLInputElement
    if (target.value?.includes(".")) {
      e.preventDefault()
    }
  }
}

// Map short names → full names for displaying in UI
export const dayNameMap: Record<string, string> = {
  Sun: "Sunday",
  Mon: "Monday",
  Tue: "Tuesday",
  Wed: "Wednesday",
  Thu: "Thursday",
  Fri: "Friday",
  Sat: "Saturday",
}

export const SEASON_LABELS = [
  "Spring (Mar-May)",
  "Summer (Jun-Aug)",
  "Fall (Sep-Nov)",
  "Winter (Dec-Feb)",
  "Holiday Season",
]

// Define preset ranges (Month is 0-based in JS Date)
const PRESET_SEASONS: Record<
  string,
  { from: { month: number; day: number }; to: { month: number; day: number } }
> = {
  "Spring (Mar-May)": {
    from: { month: 2, day: 21 }, // March 21
    to: { month: 5, day: 20 }, // Jun 20
  },
  "Summer (Jun-Aug)": {
    from: { month: 5, day: 21 }, // Jun 21
    to: { month: 8, day: 20 }, // Sep 20
  },
  "Fall (Sep-Nov)": {
    from: { month: 8, day: 21 }, // Sep 21
    to: { month: 11, day: 20 }, // Nov 20
  },
  "Winter (Dec-Feb)": {
    from: { month: 11, day: 21 }, // Dec 21
    to: { month: 2, day: 20 }, // March 20
  },
  "Holiday Season": {
    from: { month: 10, day: 15 }, // Nov 15
    to: { month: 0, day: 7 }, // Jan 7
  },
}

// Utility: get correct Date range for current or next year
export const getSeasonDates = (label: string) => {
  const today = new Date()
  const year = today.getFullYear()
  const preset = PRESET_SEASONS[label]
  if (!preset) return { from: today, to: today }

  let fromYear = year
  let toYear = year

  if (preset.to.month < preset.from.month) {
    // Cross-year season
    const decDate = new Date(year, preset.from.month, preset.from.day)
    if (decDate < today) {
      // December already passed → next year
      fromYear = year + 1
      toYear = year + 2
    } else {
      // December still future → this year
      fromYear = year
      toYear = year + 1
    }
  } else {
    // Normal season
    const toDate = new Date(year, preset.to.month, preset.to.day)
    if (toDate < today) {
      fromYear = year + 1
      toYear = year + 1
    }
  }

  const from = new Date(fromYear, preset.from.month, preset.from.day)
  const to = new Date(toYear, preset.to.month, preset.to.day)

  return { from, to }
}
